import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Heart, X, ShoppingBag } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { getWishlistEmail } from "../components/WishlistButton";

const EMAIL_KEY = "ttfm_wishlist_email";

export default function Wishlist() {
  const [params, setParams] = useSearchParams();
  const initialEmail = params.get("email") || getWishlistEmail();
  const [email, setEmail] = useState(initialEmail);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tempEmail, setTempEmail] = useState(initialEmail || "");

  // Persist URL-provided email
  useEffect(() => {
    const fromUrl = params.get("email");
    if (fromUrl && fromUrl !== getWishlistEmail()) {
      try { localStorage.setItem(EMAIL_KEY, fromUrl.toLowerCase()); } catch { /* ignore */ }
      setEmail(fromUrl.toLowerCase());
    }
  }, [params]);

  async function load(addr) {
    if (!addr) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/wishlist/by-email?email=${encodeURIComponent(addr)}`);
      setItems(data);
    } catch { toast.error("Could not load your wishlist."); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (email) load(email); }, [email]);

  async function remove(item) {
    try {
      await api.post("/wishlist/remove", {
        product_id: item.product_id,
        customer_email: email,
        variant_id: item.variant_id || null,
      });
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      toast.success("Removed from wishlist.");
    } catch { toast.error("Could not remove. Try again."); }
  }

  function submitEmail(e) {
    e.preventDefault();
    const v = (tempEmail || "").trim().toLowerCase();
    if (!v || !/.+@.+\..+/.test(v)) { toast.error("Please enter a valid email."); return; }
    try { localStorage.setItem(EMAIL_KEY, v); } catch { /* ignore */ }
    setEmail(v);
    setParams({ email: v }, { replace: true });
  }

  if (!email) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="wishlist-no-email">
        <div className="text-center">
          <Heart className="w-10 h-10 text-[var(--accent)] mx-auto" />
          <h1 className="font-serif text-3xl mt-4">Your wishlist</h1>
          <p className="text-sm text-[#5C5547] mt-2">Enter the email you used to save items. We'll pull them up for you.</p>
        </div>
        <form onSubmit={submitEmail} className="mt-6 space-y-3">
          <input type="email" value={tempEmail} onChange={(e) => setTempEmail(e.target.value)} required autoFocus
            placeholder="you@email.com"
            data-testid="wishlist-page-email-input"
            className="w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" />
          <button type="submit" data-testid="wishlist-page-email-submit"
            className="w-full py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] text-sm">
            Show my wishlist
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="wishlist-page">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-10">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Your wishlist</h1>
          <p className="text-sm text-[#5C5547] mt-1">Signed in as <span className="text-[#2C2924]">{email}</span></p>
        </div>
        <button onClick={() => { try { localStorage.removeItem(EMAIL_KEY); } catch { /* ignore */ } setEmail(""); setItems([]); setTempEmail(""); setParams({}, { replace: true }); }}
          data-testid="wishlist-signout" className="text-xs text-[#5C5547] hover:text-[var(--accent)] underline">Use a different email</button>
      </header>

      {loading && <div className="text-sm text-[#5C5547]">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="text-center py-20" data-testid="wishlist-empty">
          <Heart className="w-10 h-10 text-[#EAE4D3] mx-auto" />
          <p className="mt-4 text-[#5C5547]">No saved pieces yet.</p>
          <Link to="/shop" className="mt-6 inline-block px-6 py-2.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">Browse the shop</Link>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it) => (
            <div key={it.id} data-testid={`wishlist-item-${it.id}`}
              className="group rounded-3xl overflow-hidden border border-[#EAE4D3] bg-[#FDFBF7]">
              <Link to={`/product/${it.product_id}`} className="block relative aspect-[4/5] bg-[#F5F2EA]">
                {it.product_image && <img src={it.product_image} alt={it.product_title} className="w-full h-full object-cover transition group-hover:scale-105" />}
              </Link>
              <div className="p-4">
                <div className="font-serif text-lg leading-snug line-clamp-2">{it.product_title}</div>
                {it.variant_name && (<div className="text-xs text-[#5C5547] mt-1">{it.variant_name}</div>)}
                <div className="mt-4 flex items-center gap-2">
                  <Link to={`/product/${it.product_id}`}
                    data-testid={`wishlist-view-${it.id}`}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#2C2924] text-[#FDFBF7] text-xs">
                    <ShoppingBag className="w-3.5 h-3.5" /> View
                  </Link>
                  <button onClick={() => remove(it)} aria-label="Remove"
                    data-testid={`wishlist-remove-${it.id}`}
                    className="p-2 rounded-full hover:bg-red-50 text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
